import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraEmptyState, SpecraEntityMatchConfirm, SpecraFormField, SpecraInput, SpecraOverlayDialog, SpecraSelect, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Lock, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { RegistryEntityPicker } from '../../components/SharedRecord/RegistryEntityPicker';
import { toMatchCandidates } from '../../components/SharedRecord/toMatchCandidates';
import { EmptyState, ErrorState } from '../Delivery/_shared/components/PageStates';
import { CONFIDENCE_LEVELS, formatTs, labelOf, PURSUIT_STAKEHOLDER_AFFILIATIONS, STAKEHOLDER_ASSESSMENT_AUTHORSHIPS, STAKEHOLDER_ASSESSMENT_STATES, STAKEHOLDER_INFLUENCE_BANDS, STAKEHOLDER_RELATIONSHIP_POSTURES, tsMs } from './constants';
import { ReadOnlyScope } from './record/ReadOnlyScope';
import { useCustomerTheme, useCustomerThemes, usePursuitStakeholders, useRecordStakeholderAssessment, useResolvePursuitEntity, useStakeholderAssessments, useStakeholderPeople, useUpsertPursuitStakeholder } from './themeHooks';
const STAKEHOLDER_PARAM = 'stakeholder';
export function CaptureStakeholdersSection({ captureProjectId, correlationId, readOnly = false }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const rosterQuery = usePursuitStakeholders(captureProjectId);
    const roster = rosterQuery.data ?? [];
    const people = useStakeholderPeople(roster.some((s)=>!!s.contactId));
    const selectedId = searchParams.get(STAKEHOLDER_PARAM) ?? '';
    const [addOpen, setAddOpen] = useState(false);
    const selectStakeholder = (id)=>{
        const next = new URLSearchParams(searchParams);
        next.set(STAKEHOLDER_PARAM, id);
        setSearchParams(next, {
            replace: true
        });
    };
    return /*#__PURE__*/ _jsx(ReadOnlyScope, {
        readOnly: readOnly,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            "data-testid": Testids.capture.stakeholdersSectionRoot,
            children: [
                /*#__PURE__*/ _jsx("h3", {
                    className: "text-sm font-medium text-muted-foreground",
                    children: "Pursuit stakeholders"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-xs text-muted-foreground",
                    children: "Shared People identity is separate from these private Pursuit assessments."
                }),
                !readOnly && /*#__PURE__*/ _jsx(AddStakeholderForm, {
                    captureProjectId: captureProjectId,
                    correlationId: correlationId,
                    open: addOpen,
                    onOpenChange: setAddOpen,
                    onCreated: (s)=>{
                        selectStakeholder(s.id);
                    }
                }),
                !readOnly && roster.length > 0 && /*#__PURE__*/ _jsx("div", {
                    className: "flex justify-end",
                    children: /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: ()=>setAddOpen(true),
                        "data-testid": Testids.pursuitCustomer.addButton,
                        children: "Add stakeholder"
                    })
                }),
                rosterQuery.isLoading && /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-20"
                }),
                rosterQuery.isError && /*#__PURE__*/ _jsx(ErrorState, {
                    title: "Unable to load stakeholders",
                    error: rosterQuery.error
                }),
                roster.length > 0 && /*#__PURE__*/ _jsx("ul", {
                    className: "space-y-1",
                    children: roster.map((s)=>/*#__PURE__*/ _jsxs("li", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsxs("button", {
                                    type: "button",
                                    "data-testid": Testids.capture.stakeholderRosterRow,
                                    "data-stakeholder-id": s.id,
                                    onClick: ()=>selectStakeholder(s.id),
                                    className: `w-full rounded border px-2 py-1 text-left text-xs ${s.id === selectedId ? 'border-primary bg-muted' : ''}`,
                                    children: [
                                        stakeholderLabel(s, people.data),
                                        /*#__PURE__*/ _jsx("span", {
                                            "data-no-access": true,
                                            className: "ml-2 rounded bg-muted px-1 text-[length:var(--fs-ui-micro)] text-muted-foreground",
                                            title: "Named for reference only. Naming a contributor does not give them a login or access to sources.",
                                            children: "No access"
                                        })
                                    ]
                                }),
                                !s.contactId && (s.registryCandidateId || s.resolution) && /*#__PURE__*/ _jsx(StakeholderMatchPrompt, {
                                    stakeholder: s,
                                    captureProjectId: captureProjectId,
                                    correlationId: correlationId
                                })
                            ]
                        }, s.id))
                }),
                !rosterQuery.isLoading && !rosterQuery.isError && roster.length === 0 && !selectedId ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    size: "compact",
                    title: "No stakeholders yet",
                    description: "Add the customer contacts who shape this pursuit.",
                    "data-testid": Testids.pursuitCustomer.emptyState,
                    action: readOnly ? undefined : /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: ()=>setAddOpen(true),
                        "data-testid": Testids.pursuitCustomer.addButton,
                        children: "Add stakeholder"
                    })
                }) : selectedId ? /*#__PURE__*/ _jsx(StakeholderDetail, {
                    captureProjectId: captureProjectId,
                    stakeholderId: selectedId,
                    rosterEntry: roster.find((r)=>r.id === selectedId),
                    correlationId: correlationId
                }) : /*#__PURE__*/ _jsx(EmptyState, {
                    title: "No stakeholder selected",
                    description: "Add a stakeholder, or select one from the roster, to view or record its assessment history."
                })
            ]
        })
    });
}
/** A linked row holds no typed copy (GRW-37): its label comes from People. */ function stakeholderLabel(s, people) {
    if (s.contactId) {
        const p = people?.get(s.contactId);
        if (p) return [
            p.name,
            p.organization
        ].filter(Boolean).join(' · ');
        return s.unresolvedName || 'Linked person';
    }
    return [
        s.unresolvedName || 'Unnamed stakeholder',
        s.unresolvedOrganization
    ].filter(Boolean).join(' · ') || 'Unnamed stakeholder';
}
function splitPersonName(text) {
    const name = text.trim().replace(/\s+/g, ' ');
    const i = name.lastIndexOf(' ');
    return i > 0 ? {
        firstName: name.slice(0, i),
        lastName: name.slice(i + 1)
    } : {
        firstName: '',
        lastName: name
    };
}
/**
 * GRW-37: "Is this John Smith (DISA)?" on an unresolved stakeholder. Yes /
 * This one links the contact; "None of these — create new" adds the person to
 * Registries; "Decide later" leaves the candidate in the registry review queue.
 */ function StakeholderMatchPrompt({ stakeholder, captureProjectId, correlationId }) {
    const resolve = useResolvePursuitEntity(correlationId);
    const [declined, setDeclined] = useState(false);
    const [deferred, setDeferred] = useState(false);
    const candidates = toMatchCandidates(stakeholder.resolution?.candidates);
    const decide = (decision, registryId)=>resolve.mutate({
            captureProjectId,
            linkKind: 'STAKEHOLDER',
            linkId: stakeholder.id,
            decision,
            registryId
        });
    return /*#__PURE__*/ _jsx(SpecraEntityMatchConfirm, {
        kind: "CONTACT",
        size: "xs",
        density: "inline",
        showUnresolvedBadge: true,
        "data-testid": Testids.capture.stakeholderMatchPrompt,
        observedLabel: stakeholder.unresolvedName || stakeholder.resolution?.displayName || 'This person',
        observedContext: stakeholder.unresolvedOrganization || undefined,
        candidates: candidates,
        state: resolve.isPending ? 'pending' : resolve.isError ? 'error' : deferred ? 'deferred' : declined ? 'declined' : 'ask',
        errorMessage: "Couldn't link this person. Try again.",
        createLabel: "None of these — create new",
        onConfirm: (id)=>decide('PICK', id),
        onDecline: ()=>setDeclined(true),
        onCreateNew: ()=>decide('CREATE_NEW'),
        onDefer: ()=>setDeferred(true)
    });
}
function AddStakeholderForm({ captureProjectId, correlationId, open, onOpenChange, onCreated }) {
    const upsert = useUpsertPursuitStakeholder(correlationId);
    const [contactId, setContactId] = useState('');
    const [details, setDetails] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [organization, setOrganization] = useState('');
    const [affiliation, setAffiliation] = useState(PURSUIT_STAKEHOLDER_AFFILIATIONS[0].value);
    const [pursuitRole, setPursuitRole] = useState('');
    const typed = details && (!!name.trim() || !!email.trim());
    const ready = !!contactId || typed;
    const submit = ()=>{
        if (!ready || upsert.isPending) return;
        upsert.mutate({
            captureProjectId,
            contactId,
            // GRW-37: a typed person travels as a PersonDraft; zou resolves it
            // through REG-54 (an email links with no prompt, a name asks).
            personDraft: !contactId && typed ? {
                ...splitPersonName(name),
                email: email.trim(),
                organization: organization.trim()
            } : undefined,
            affiliation,
            pursuitRole
        }, {
            onSuccess: (resp)=>{
                if (resp.stakeholder) onCreated(resp.stakeholder);
                setContactId('');
                setName('');
                setEmail('');
                setOrganization('');
                setPursuitRole('');
                setDetails(false);
                onOpenChange(false);
            }
        });
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: open,
        onOpenChange: onOpenChange,
        title: "Add stakeholder",
        size: "lg",
        testId: Testids.pursuitCustomer.dialog,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                !details && /*#__PURE__*/ _jsx(RegistryEntityPicker, {
                    kinds: [
                        'CONTACT'
                    ],
                    label: "Stakeholder",
                    placeholder: "Search people in Registries",
                    value: contactId,
                    context: {
                        kind: 'PURSUIT',
                        id: captureProjectId
                    },
                    createMode: "none",
                    onPick: (o)=>setContactId(o.registryId)
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "link",
                    size: "sm",
                    className: "h-auto px-0",
                    "data-testid": Testids.capture.stakeholderAddDetailsLink,
                    onClick: ()=>{
                        setDetails((d)=>!d);
                        setContactId('');
                    },
                    children: details ? 'Pick someone in Registries instead' : 'Not in Registries? Add details'
                }),
                details && /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-1 gap-3 sm:grid-cols-3",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Name",
                            htmlFor: "stakeholder-add-name",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "stakeholder-add-name",
                                value: name,
                                onChange: (e)=>setName(e.target.value),
                                placeholder: "First and last name",
                                "data-testid": Testids.capture.stakeholderAddNameInput
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Email",
                            htmlFor: "stakeholder-add-email",
                            hint: "With an email we can match it to an existing person.",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "stakeholder-add-email",
                                type: "email",
                                value: email,
                                onChange: (e)=>setEmail(e.target.value),
                                "data-testid": Testids.capture.stakeholderAddEmailInput
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Organization",
                            htmlFor: "stakeholder-add-org",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "stakeholder-add-org",
                                value: organization,
                                onChange: (e)=>setOrganization(e.target.value),
                                placeholder: "e.g. DISA",
                                "data-testid": Testids.capture.stakeholderAddOrganizationInput
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-1 items-end gap-3 sm:grid-cols-[10rem_1fr_auto]",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Affiliation",
                            htmlFor: "stakeholder-add-affiliation",
                            children: /*#__PURE__*/ _jsx(SpecraSelect, {
                                id: "stakeholder-add-affiliation",
                                value: affiliation,
                                onChange: (e)=>setAffiliation(e.target.value),
                                "data-testid": Testids.capture.stakeholderAddAffiliationSelect,
                                children: PURSUIT_STAKEHOLDER_AFFILIATIONS.map((o)=>/*#__PURE__*/ _jsx("option", {
                                        value: o.value,
                                        children: o.label
                                    }, o.value))
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Pursuit role",
                            htmlFor: "stakeholder-add-role",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "stakeholder-add-role",
                                value: pursuitRole,
                                onChange: (e)=>setPursuitRole(e.target.value),
                                placeholder: "e.g. Contracting officer",
                                "data-testid": Testids.capture.stakeholderAddPursuitRoleInput
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            onClick: submit,
                            disabled: !ready || upsert.isPending,
                            "data-testid": Testids.capture.stakeholderAddSubmitButton,
                            iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                className: "h-4 w-4"
                            }),
                            children: "Add stakeholder"
                        })
                    ]
                })
            ]
        })
    });
}
function StakeholderDetail({ captureProjectId, stakeholderId, rosterEntry, correlationId }) {
    const assessments = useStakeholderAssessments(captureProjectId, stakeholderId);
    const people = useStakeholderPeople(!!rosterEntry?.contactId);
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-stakeholder-id": stakeholderId,
        children: [
            /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                className: "py-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardTitle, {
                        children: rosterEntry?.contactId ? stakeholderLabel(rosterEntry, people.data) : rosterEntry?.unresolvedName || `Stakeholder ${stakeholderId}`
                    }),
                    !rosterEntry?.contactId && rosterEntry?.unresolvedOrganization && /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-muted-foreground",
                        children: rosterEntry.unresolvedOrganization
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-3",
                children: [
                    rosterEntry && /*#__PURE__*/ _jsx(StakeholderRoleEditor, {
                        stakeholder: rosterEntry,
                        captureProjectId: captureProjectId
                    }, rosterEntry.id),
                    rosterEntry?.contactId && /*#__PURE__*/ _jsx(Link, {
                        className: "text-xs underline",
                        "data-testid": Testids.capture.stakeholderOpenPerson,
                        to: `/registries?tab=contacts&record=${encodeURIComponent(rosterEntry.contactId)}`,
                        children: "Open Person"
                    }),
                    !!rosterEntry?.roleHistory?.length && /*#__PURE__*/ _jsxs("section", {
                        "aria-label": "Pursuit role history",
                        "data-testid": Testids.capture.stakeholderRoleHistory,
                        children: [
                            /*#__PURE__*/ _jsx("h4", {
                                className: "text-xs font-medium",
                                children: "Pursuit role history"
                            }),
                            /*#__PURE__*/ _jsx("ol", {
                                className: "space-y-1 text-xs",
                                children: rosterEntry.roleHistory.map((change, index)=>/*#__PURE__*/ _jsxs("li", {
                                        children: [
                                            change.previousRole || 'No role',
                                            " → ",
                                            change.newRole || 'No role',
                                            " ·",
                                            ' ',
                                            formatTs(change.changedAt)
                                        ]
                                    }, `${change.changedAt?.toJsonString()}-${index}`))
                            })
                        ]
                    }),
                    assessments.isLoading ? /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-16"
                    }) : assessments.isError ? /*#__PURE__*/ _jsx(ErrorState, {
                        title: "Unable to load this stakeholder's assessments",
                        error: assessments.error
                    }) : (assessments.data ?? []).length === 0 ? /*#__PURE__*/ _jsx(EmptyState, {
                        title: "No assessments recorded yet",
                        description: "Record the first assessment below — influence band and rationale are required."
                    }) : /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-2",
                        children: [
                            ...assessments.data ?? []
                        ].sort((a, b)=>tsMs(b.createdAt) - tsMs(a.createdAt)).map((a)=>/*#__PURE__*/ _jsx(AssessmentRow, {
                                assessment: a,
                                captureProjectId: captureProjectId
                            }, a.id))
                    }),
                    /*#__PURE__*/ _jsx(RecordAssessmentForm, {
                        captureProjectId: captureProjectId,
                        stakeholderId: stakeholderId,
                        correlationId: correlationId
                    })
                ]
            })
        ]
    });
}
function AssessmentRow({ assessment, captureProjectId }) {
    // All five sensitive fields blank is only ever reachable by the masking
    // path in ListStakeholderAssessments — RecordAssessmentForm below never
    // lets influenceBand or rationale leave this surface blank, so this
    // condition is unambiguous, never a false positive from an honest gap.
    const withheld = !assessment.influenceBand && !assessment.relationshipPosture && !assessment.priorities && !assessment.rationale && !assessment.uncertaintyNote;
    return /*#__PURE__*/ _jsxs("li", {
        "data-testid": Testids.capture.stakeholderAssessmentRow,
        "data-assessment-id": assessment.id,
        className: "space-y-2 rounded border p-2 text-xs",
        children: [
            /*#__PURE__*/ _jsxs("p", {
                className: "text-muted-foreground",
                children: [
                    labelOf(CONFIDENCE_LEVELS, assessment.confidence),
                    " confidence ·",
                    ' ',
                    assessment.authorship === 'ai_suggested' ? 'AI-suggested' : 'human',
                    " ·",
                    ' ',
                    labelOf(STAKEHOLDER_ASSESSMENT_STATES, assessment.assessmentState),
                    assessment.sourceKind ? ` · ${assessment.sourceKind}` : '',
                    assessment.sourceRef ? ` (${assessment.sourceRef})` : '',
                    " · as of",
                    ' ',
                    formatTs(assessment.asOfDate),
                    " · created ",
                    formatTs(assessment.createdAt),
                    assessment.createdBy ? ` by ${assessment.createdBy}` : ''
                ]
            }),
            withheld ? /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.capture.stakeholderAssessmentWithheldBox,
                className: "flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-amber-900",
                children: [
                    /*#__PURE__*/ _jsx(Lock, {
                        className: "mt-0.5 h-3.5 w-3.5 shrink-0",
                        "aria-hidden": true
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: "Pursuit intelligence withheld — this assessment exists, but you don't have permission to view its contents."
                    })
                ]
            }) : /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1",
                children: [
                    assessment.pursuitRole && /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Role: ",
                            assessment.pursuitRole
                        ]
                    }),
                    (assessment.themeIds ?? []).map((id)=>/*#__PURE__*/ _jsx(AssessmentThemeLink, {
                            captureProjectId: captureProjectId,
                            themeId: id
                        }, id)),
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-semibold",
                                children: "Influence:"
                            }),
                            ' ',
                            labelOf(STAKEHOLDER_INFLUENCE_BANDS, assessment.influenceBand)
                        ]
                    }),
                    assessment.relationshipPosture && /*#__PURE__*/ _jsxs("p", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-semibold",
                                children: "Posture:"
                            }),
                            ' ',
                            labelOf(STAKEHOLDER_RELATIONSHIP_POSTURES, assessment.relationshipPosture)
                        ]
                    }),
                    assessment.priorities && /*#__PURE__*/ _jsxs("p", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-semibold",
                                children: "Priorities:"
                            }),
                            " ",
                            assessment.priorities
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-semibold",
                                children: "Rationale:"
                            }),
                            " ",
                            assessment.rationale
                        ]
                    }),
                    assessment.uncertaintyNote && /*#__PURE__*/ _jsxs("p", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-semibold",
                                children: "Uncertainty:"
                            }),
                            " ",
                            assessment.uncertaintyNote
                        ]
                    })
                ]
            })
        ]
    });
}
function RecordAssessmentForm({ captureProjectId, stakeholderId, correlationId }) {
    const record = useRecordStakeholderAssessment(correlationId);
    const [influenceBand, setInfluenceBand] = useState(STAKEHOLDER_INFLUENCE_BANDS[0].value);
    const [relationshipPosture, setRelationshipPosture] = useState(STAKEHOLDER_RELATIONSHIP_POSTURES[0].value);
    const [priorities, setPriorities] = useState('');
    const [rationale, setRationale] = useState('');
    const [uncertaintyNote, setUncertaintyNote] = useState('');
    const [confidence, setConfidence] = useState(CONFIDENCE_LEVELS[0].value);
    const [sourceKind, setSourceKind] = useState('');
    const [sourceRef, setSourceRef] = useState('');
    const [authorship, setAuthorship] = useState(STAKEHOLDER_ASSESSMENT_AUTHORSHIPS[0].value);
    const [assessmentState, setAssessmentState] = useState(STAKEHOLDER_ASSESSMENT_STATES[0].value);
    const [asOfDate, setAsOfDate] = useState('');
    const themes = useCustomerThemes(captureProjectId);
    const [themeIds, setThemeIds] = useState([]);
    const trimmedRationale = rationale.trim();
    const guardMessage = !influenceBand ? 'Influence band is required before this assessment can be saved.' : !trimmedRationale ? 'Rationale is required before this assessment can be saved.' : '';
    const submit = ()=>{
        if (!influenceBand || !trimmedRationale || record.isPending) return;
        record.mutate({
            captureProjectId,
            stakeholderId,
            themeIds,
            influenceBand,
            relationshipPosture,
            priorities,
            rationale: trimmedRationale,
            uncertaintyNote,
            confidence,
            sourceKind,
            sourceRef,
            authorship,
            assessmentState,
            asOfDate: asOfDate ? new Date(asOfDate) : null
        }, {
            onSuccess: ()=>{
                setPriorities('');
                setRationale('');
                setUncertaintyNote('');
                setSourceRef('');
            }
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2 border-t pt-3",
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs font-medium",
                children: "Record assessment"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Link relevant customer themes; selections are retained with this assessment."
            }),
            themes.isLoading && /*#__PURE__*/ _jsx(Skeleton, {
                className: "h-10"
            }),
            themes.isError && /*#__PURE__*/ _jsx(ErrorState, {
                title: "Unable to load theme choices",
                error: themes.error
            }),
            (themes.data ?? []).map((theme)=>/*#__PURE__*/ _jsx(ThemeChoice, {
                    captureProjectId: captureProjectId,
                    themeId: theme.id,
                    selected: themeIds.includes(theme.id),
                    onToggle: ()=>setThemeIds((prev)=>prev.includes(theme.id) ? prev.filter((id)=>id !== theme.id) : [
                                ...prev,
                                theme.id
                            ])
                }, theme.id)),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-end gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-36 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Influence band"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: influenceBand,
                                onValueChange: setInfluenceBand,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.capture.stakeholderAssessmentInfluenceBandSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: STAKEHOLDER_INFLUENCE_BANDS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-36 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Relationship posture"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: relationshipPosture,
                                onValueChange: setRelationshipPosture,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.capture.stakeholderAssessmentRelationshipPostureSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: STAKEHOLDER_RELATIONSHIP_POSTURES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-32 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Confidence"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: confidence,
                                onValueChange: setConfidence,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.capture.stakeholderAssessmentConfidenceSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: CONFIDENCE_LEVELS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-36 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Authorship"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: authorship,
                                onValueChange: setAuthorship,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.capture.stakeholderAssessmentAuthorshipSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: STAKEHOLDER_ASSESSMENT_AUTHORSHIPS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-32 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "State"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: assessmentState,
                                onValueChange: setAssessmentState,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.capture.stakeholderAssessmentStateSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: STAKEHOLDER_ASSESSMENT_STATES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-40 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Source kind"
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                value: sourceKind,
                                onChange: (e)=>setSourceKind(e.target.value),
                                placeholder: "e.g. meeting",
                                "data-testid": Testids.capture.stakeholderAssessmentSourceKindInput
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-[140px] flex-1 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Source reference"
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                value: sourceRef,
                                onChange: (e)=>setSourceRef(e.target.value),
                                placeholder: "Link or citation",
                                "data-testid": Testids.capture.stakeholderAssessmentSourceRefInput
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-40 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "As of date"
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                type: "date",
                                value: asOfDate,
                                onChange: (e)=>setAsOfDate(e.target.value),
                                "data-testid": Testids.capture.stakeholderAssessmentAsOfDateInput
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1",
                children: [
                    /*#__PURE__*/ _jsx("label", {
                        className: "text-xs font-medium",
                        children: "Priorities"
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: priorities,
                        onChange: (e)=>setPriorities(e.target.value),
                        rows: 1,
                        "data-testid": Testids.capture.stakeholderAssessmentPrioritiesInput
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1",
                children: [
                    /*#__PURE__*/ _jsx("label", {
                        className: "text-xs font-medium",
                        children: "Rationale"
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: rationale,
                        onChange: (e)=>setRationale(e.target.value),
                        rows: 2,
                        "data-testid": Testids.capture.stakeholderAssessmentRationaleInput
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1",
                children: [
                    /*#__PURE__*/ _jsx("label", {
                        className: "text-xs font-medium",
                        children: "Uncertainty note"
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: uncertaintyNote,
                        onChange: (e)=>setUncertaintyNote(e.target.value),
                        rows: 1,
                        "data-testid": Testids.capture.stakeholderAssessmentUncertaintyNoteInput
                    })
                ]
            }),
            guardMessage && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-amber-700",
                children: guardMessage
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                onClick: submit,
                disabled: !influenceBand || !trimmedRationale || record.isPending,
                "data-testid": Testids.capture.stakeholderAssessmentSubmitButton,
                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                    className: "h-3.5 w-3.5"
                }),
                children: "Save assessment"
            })
        ]
    });
}
function StakeholderRoleEditor({ stakeholder, captureProjectId }) {
    const save = useUpsertPursuitStakeholder();
    const [role, setRole] = useState(stakeholder.pursuitRole);
    const [contactId, setContactId] = useState(stakeholder.contactId);
    const [name, setName] = useState(stakeholder.unresolvedName);
    const [organization, setOrganization] = useState(stakeholder.unresolvedOrganization);
    const unchanged = role === stakeholder.pursuitRole && contactId === stakeholder.contactId && name === stakeholder.unresolvedName && organization === stakeholder.unresolvedOrganization;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsx(RegistryEntityPicker, {
                kinds: [
                    'CONTACT'
                ],
                label: "Linked person",
                placeholder: "Search people in Registries",
                value: contactId,
                context: {
                    kind: 'PURSUIT',
                    id: captureProjectId
                },
                createMode: "none",
                onPick: (o)=>setContactId(o.registryId)
            }),
            !contactId && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        "aria-label": "Stakeholder display name",
                        value: name,
                        onChange: (event)=>setName(event.target.value)
                    }),
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        "aria-label": "Stakeholder organization",
                        value: organization,
                        onChange: (event)=>setOrganization(event.target.value)
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraInput, {
                "aria-label": "Pursuit role",
                value: role,
                onChange: (e)=>setRole(e.target.value)
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                disabled: save.isPending || unchanged || !name.trim() && !contactId,
                onClick: ()=>{
                    if (save.isPending) return;
                    save.mutate({
                        id: stakeholder.id,
                        captureProjectId,
                        contactId,
                        unresolvedName: contactId ? '' : name,
                        unresolvedOrganization: contactId ? '' : organization,
                        affiliation: stakeholder.affiliation,
                        pursuitRole: role
                    });
                },
                children: "Save stakeholder"
            })
        ]
    });
}
function ThemeChoice({ captureProjectId, themeId, selected, onToggle }) {
    const detail = useCustomerTheme(captureProjectId, themeId);
    const current = detail.data?.versions.find((v)=>v.id === detail.data?.theme?.currentVersionId);
    return /*#__PURE__*/ _jsx(SpecraButton, {
        size: "sm",
        variant: selected ? 'default' : 'secondary',
        disabled: !current,
        "aria-pressed": selected,
        onClick: onToggle,
        children: current?.title || 'Loading theme…'
    });
}
function AssessmentThemeLink({ captureProjectId, themeId }) {
    const detail = useCustomerTheme(captureProjectId, themeId);
    const current = detail.data?.versions.find((v)=>v.id === detail.data?.theme?.currentVersionId);
    return current ? /*#__PURE__*/ _jsx("a", {
        className: "underline",
        href: `#theme-${encodeURIComponent(themeId)}`,
        children: current.title
    }) : /*#__PURE__*/ _jsx("span", {
        children: "Theme unavailable"
    });
}
